import { getSnapshot, Instance, types } from 'mobx-state-tree'
import { createContext, useContext } from 'react'
import RootStore from '../../../../../../store/RootStore'
import { IDraftModel } from '@linqpal/models'
import { CoOwner, EditableCoOwnerModel } from './CoOwner'
import ApplicationStore from '../../../ApplicationStore'

const CoOwnersListModel = types
  .model({
    coOwners: types.optional(types.array(EditableCoOwnerModel), []),
  })
  .views((self) => ({
    get isFilled() {
      const allEmailsUnique =
        new Set(self.coOwners.map((c) => c.email)).size === self.coOwners.length

      const allCoOwnersFilled =
        self.coOwners.length === 0 || self.coOwners.every((c) => c.isFilled)

      return (
        allCoOwnersFilled &&
        allEmailsUnique &&
        ApplicationStore.totalOwnershipPercentage <= 100
      )
    },
    get hasCoOwners() {
      return self.coOwners.length > 0
    },
    get coOwnersCount() {
      return self.coOwners.length
    },
    get(index: number) {
      return self.coOwners[index]
    },
  }))
  .actions((self) => ({
    fill(doc: IDraftModel) {
      self.coOwners.clear()
      const draftCoOwners = [1, 2, 3, 4] // can have up to 4 co-owners, 25% each
        .map((index) => doc?.get('coOwnerInfo', `coOwner${index}`))
        .filter((item) => item)
      draftCoOwners.forEach((item) => this.add(item))
    },
    add(coOwner: CoOwner) {
      self.coOwners.push(coOwner)

      const newIndex = self.coOwners.length - 1
      self.coOwners[newIndex].key = newIndex + new Date().getTime().toString()

      return newIndex
    },
    removeAt(index: number) {
      self.coOwners.splice(index, 1)
    },
    updateAt(index: number, coOwner: CoOwner) {
      self.coOwners[index] = coOwner
    },
    updateDraft() {
      const draft = RootStore.userStore.document

      // use coOwners field to save Filled property to let navigation controls / review page work
      draft?.setContent(
        'coOwnerInfo',
        `coOwners`,
        self.hasCoOwners,
        self.isFilled,
      )

      // remove draft co-owners which have been removed from store,
      // 4 is the maximum number of co-owners with 25% each
      for (let i = self.coOwners.length + 1; i <= 4; i++) {
        draft?.clearItems([`coOwnerInfo.coOwner${i}`])
      }

      // rewrite co-owners in draft with co-owners from store
      for (let i = 0; i < self.coOwners.length; i++) {
        const coOwner = self.coOwners[i]
        const snapshot = getSnapshot(coOwner)

        draft?.setContent(
          'coOwnerInfo',
          `coOwner${i + 1}`,
          { ...snapshot, key: undefined },
          coOwner.isFilled,
        )
      }
    },
  }))

type CoOwnersList = Instance<typeof CoOwnersListModel>
const CoOwnersContext = createContext<CoOwnersList>(CoOwnersListModel.create())

export function useCoOwnerStore() {
  return useContext(CoOwnersContext)
}
